import dayjs from 'dayjs';
import {useCallback, useMemo, useState} from 'react';
import {DATE_FORMAT} from '../../../shared/utils/date';
import {store} from '../../store';
import {
  selectDays,
  selectIsMoreDaysInFuture,
  selectLastDate,
  selectPaginationFutureRequestStatus,
  selectSelectedDate,
} from '../../store/slices/selectors';
import {useAppSelector} from '../../hooks/store';
import {getActiveWeek} from '../CalendarNavigation/getActiveWeek';
import {getWeekChunks} from '../CalendarNavigation/getWeekChunks';
import {ApiError, useDevice, useNotify, useViewport} from '@treatwell/ui';
import {
  dispatchDateSelect,
  dispatchFetchAvailability,
} from '../../store/slices/dispatch';
import {trackCalendarArrow} from '../../utils/gtmTracking';
import {trackDateSelectedEvent} from '../CalendarNavigation/trackDateSelectedEvent';
import {t} from 'i18next';
import {handleUnexpectedResourceAccess} from '../../utils/handleUnexpectedResourceAccess';
import {useInView} from 'react-intersection-observer';
import DayList from '../DayList/DayList';
import DayListNavigationBar from '../DayListNavigationBar/DayListNavigationBar';

export const DayListNavigation = () => {
  const selectedDate = useAppSelector(selectSelectedDate);
  const days = useAppSelector(selectDays);
  const paginationFutureRequestStatus = useAppSelector(
    selectPaginationFutureRequestStatus,
  );
  const lastDate = useAppSelector(selectLastDate);
  const isMoreDaysInFuture = useAppSelector(selectIsMoreDaysInFuture);
  const {isMobile} = useDevice();
  const {notifyError} = useNotify();
  const weeks = useMemo(() => getWeekChunks(Object.values(days)), [days]);
  const activeWeek = useMemo(
    () => getActiveWeek(weeks, selectedDate),
    [weeks, selectedDate],
  );
  const isSmallViewport = useViewport({device: ['mobile', 'tablet']});
  const isFirstWeek = activeWeek === 0;
  const isLastWeek = activeWeek === weeks.length - 1;

  const handleDispatchDateSelect = useCallback((date: string) => {
    dispatchDateSelect({
      date,
    });
  }, []);

  const handleDaySelect = useCallback(
    (date: string) => {
      handleDispatchDateSelect(date);
    },
    [handleDispatchDateSelect],
  );

  const handleNavigateNext = () => {
    const nextDate = dayjs(selectedDate, DATE_FORMAT)
      .add(7, 'days')
      .format(DATE_FORMAT);
    const startDate = dayjs(lastDate).add(1, 'day').format('YYYY-MM-DD');

    {
      // Redefine `isLastWeek` here to subtract 2 from the weeks
      // as the values in this function are one behind those in the parent scope
      // because this click handler runs before they update.
      // Other branches below do not need to do this as there are no more weeks
      // added in those cases so the length does not increase
      const isLastWeek = activeWeek === weeks.length - 2;

      // If a user is on the last week we scroll them to the nextDate and request the next
      // available weeks
      if (isLastWeek && isMoreDaysInFuture) {
        trackCalendarArrow(nextDate);
        handleDispatchDateSelect(nextDate);
        dispatchFetchAvailability({startDate});
        trackDateSelectedEvent({store, isMobile, action: 'right arrow'});
        return;
      }
    }

    const lastPaginationRequestFailed =
      paginationFutureRequestStatus === 'rejected';

    // if a user is on the last week and the user cannot navigate to the nextDate
    // and the pagination request failed, allow the user to request the availability again
    if (isLastWeek && lastPaginationRequestFailed && isMoreDaysInFuture) {
      dispatchFetchAvailability({startDate})
        .unwrap()
        .then(() => {
          handleDispatchDateSelect(nextDate);
          trackDateSelectedEvent({store, isMobile, action: 'right arrow'});
        })
        .catch((err) => {
          const error = err as Error | ApiError;
          handlePaginationError(error);
        });
      return;
    }

    // capture the usual scenario for navigating to next week
    trackCalendarArrow(nextDate);
    handleDispatchDateSelect(nextDate);
    trackDateSelectedEvent({store, isMobile, action: 'right arrow'});
  };

  const handleNavigatePrevious = () => {
    const nextDate = dayjs(selectedDate, DATE_FORMAT)
      .subtract(7, 'days')
      .format(DATE_FORMAT);

    trackCalendarArrow(nextDate);
    handleDispatchDateSelect(nextDate);
    trackDateSelectedEvent({store, isMobile, action: 'left arrow'});
  };

  const handlePaginationError = (error: Error | ApiError) => {
    // rainbow throws unexpected resource_access when we request a startDate that
    // does not exist in our database, which means there is no more
    // availability to paginate through.
    handleUnexpectedResourceAccess({
      errorMessage: error.message,
      onMatchedError: () => {
        return;
      },
      onAnyOtherError: () => {
        notifyError(t('error.header'));
      },
    });
  };

  const [calendarHeight, setCalendarHeight] = useState(0);
  const {ref: calendarRef} = useInView({
    rootMargin: `-${calendarHeight + 1}px 0px 0px 0px`,
    initialInView: true,
  });

  const handleOnSelected = useCallback(
    (readableDate: string) => {
      handleDaySelect(readableDate);
      trackDateSelectedEvent({store, isMobile, action: 'click'});
    },
    [handleDaySelect, isMobile],
  );

  return (
    <div
      ref={(node) => {
        if (!node || isSmallViewport) {
          return;
        }
        calendarRef(node);
        const calendarHeight = node.getBoundingClientRect().height;
        setCalendarHeight(calendarHeight);
      }}
    >
      <DayListNavigationBar
        selectedDate={selectedDate}
        formattedMonth={dayjs(selectedDate, DATE_FORMAT).format('MMMM')}
        isLastWeek={isLastWeek}
        isFirstWeek={isFirstWeek}
        paginationFutureRequestStatus={paginationFutureRequestStatus}
        onNavigateNext={handleNavigateNext}
        onNavigatePrevious={handleNavigatePrevious}
      />
      <DayList
        weeks={weeks}
        selectedDate={selectedDate}
        onSelected={handleOnSelected}
        activeWeek={activeWeek}
        onNavigateNext={handleNavigateNext}
        onNavigatePrevious={handleNavigatePrevious}
        isLastWeek={isLastWeek}
        isFirstWeek={isFirstWeek}
        paginationFutureRequestStatus={paginationFutureRequestStatus}
      />
    </div>
  );
};
